// Growth Ops Admin Types

export interface GrowthProvider {
  id: string;
  businessName: string;
  ownerName: string;
  email: string;
  subscriptionPlan: string;
  walletBalance: number;
  growthAdsEnabled: boolean;
  growthAdsPaused: boolean;
  maxLeadsPerWeek: number;
  activeCampaigns: number;
  leadsThisWeek: number;
  weeklySpend: number;
  /** Per-day burn rate including 10% platform fee. */
  dailyBurn: number;
  /** Wallet balance threshold below which campaigns auto-pause (one day burn + 15% buffer). */
  pauseThreshold: number;
}

export interface GrowthProviderRow {
  id: string;
  business_name: string;
  owner_name: string;
  email: string;
  subscription_plan: string;
  wallet_balance: number;
  growth_ads_enabled: boolean;
  growth_ads_paused: boolean;
  max_leads_per_week: number;
}

export interface OptimizerAction {
  id: number;
  businessId: string;
  businessName: string;
  campaignId: string | null;
  actionType: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface OptimizerActionRow {
  id: number;
  business_id: string;
  campaign_id: string | null;
  action_type: string;
  details: Record<string, unknown>;
  created_at: string;
  businesses: { business_name: string } | null;
}

export interface GrowthMetrics {
  totalProviders: number;
  activeProviders: number;
  pausedProviders: number;
  totalWalletBalance: number;
  totalLifetimeAdSpend: number;
  totalLifetimePlatformFee: number;
  leadsThisWeek: number;
  weeklyRevenue: number;
}

export const ACTION_TYPE_LABELS: Record<string, string> = {
  pause: 'Campaign Paused',
  resume: 'Campaign Resumed',
  budget_adjust: 'Budget Adjusted',
  geo_update: 'Geo Targeting Updated',
  creative_swap: 'Creative Swapped',
  cap_check: 'Cap Check',
  wallet_check: 'Wallet Check',
  error: 'Error',
  daily_reset: 'Daily Reset',
  weekly_cap_reached: 'Weekly Cap Reached',
};

// ─── Per-network reporting (Issue #440) ────────────────────────────────────

export type AdNetwork = 'meta' | 'google_ads' | 'google_lsa';

export interface NetworkCampaignRow {
  id: string;
  name: string;
  network: AdNetwork;
  status: 'draft' | 'active' | 'paused' | 'archived';
  dailyBudget: number;
  totalSpend: number;
  totalLeads: number;
}

export interface NetworkCpaqRow {
  network: AdNetwork;
  cpaqTrailing7d: number | null;
  cpaqTrailing30d: number | null;
}

export interface AllocatorDecision {
  id: string;
  decidedAt: string;
  metaPct: number;
  googlePct: number;
  lsaPct: number;
  rebalanceReason: string;
}

export interface PerNetworkReport {
  businessId: string;
  businessName: string;
  campaigns: NetworkCampaignRow[];
  cpaq: NetworkCpaqRow[];
  allocatorDecisions: AllocatorDecision[];
  /** Current allocation percentages */
  currentStrategy: {
    metaPct: number;
    googlePct: number;
    lsaPct: number;
    mode: 'rules' | 'ml';
  } | null;
}

// ─── Observability (Issue #441) ─────────────────────────────────────────────

export interface ObservabilityMetrics {
  /** Daily spend per network for last 30 days */
  dailySpendByNetwork: Array<{
    date: string;
    meta: number;
    googleAds: number;
    googleLsa: number;
  }>;
  /** Per-network CPAQ trailing 7d (across all providers) */
  cpaqByNetwork: Array<{ network: AdNetwork; cpaqTrailing7d: number | null }>;
  /** Allocator decisions count per day (last 30 days) */
  allocatorDecisionsPerDay: Array<{ date: string; count: number }>;
  /** Google Ads API error count from optimizer_actions (last 7 days) */
  googleAdsErrorCount: number;
  /** LSA charge sync drift: lead_attribution count vs lsa_lead_charge wallet txns (last 7 days) */
  lsaChargeDrift: { leadAttributionCount: number; walletChargeCount: number; drift: number };
}

// ─── Observability (Issue #441) ─────────────────────────────────────────────

export interface ObservabilityMetrics {
  /** Daily spend per network for last 30 days */
  dailySpendByNetwork: Array<{
    date: string;
    meta: number;
    googleAds: number;
    googleLsa: number;
  }>;
  /** Per-network CPAQ trailing 7d (across all providers) */
  cpaqByNetwork: Array<{ network: AdNetwork; cpaqTrailing7d: number | null }>;
  /** Allocator decisions count per day (last 30 days) */
  allocatorDecisionsPerDay: Array<{ date: string; count: number }>;
  /** Google Ads API error count from optimizer_actions (last 7 days) */
  googleAdsErrorCount: number;
  /** LSA charge sync drift: lead_attribution count vs lsa_lead_charge wallet txns (last 7 days) */
  lsaChargeDrift: { leadAttributionCount: number; walletChargeCount: number; drift: number };
}
